import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useState} from "react";
import {CalendarPanel} from "./calendarPanel";
import {HourType, TimePicker} from "./timePicker";
import {Popover, PopoverContent, PopoverTrigger} from "../popover";
import {Button} from "../button";
import {cn, formatDate} from "../../lib/fns";
import {SwapRight} from "../../assets/svgs/SwapRight";
import {CalendarSvg} from "../../assets/svgs/CalendarSvg";

export interface DateTimeRange {
  startDate: Date | null
  endDate: Date | null
  startTime: HourType | null
  endTime: HourType | null
}

export interface DateRange {
  startDate: Date | null
  endDate: Date | null
}

interface DateTimeRangePickerProps {
  startDate?: Date,
  endDate?: Date,
  onChange: (startDate: Date | null, endDate: Date | null) => void
  placeholder?: [string, string]
  format?: string
  showTime?: boolean
  disabled?: boolean
  className?: string
}

const today = new Date();
export const DEFAULT_START_DATE = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 10, 0, 0);

// End: yesterday 03:00
export const DEFAULT_END_DATE = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1, 3, 0, 0);
const DEFAULT_START_TIME = {hour: 10, minute: 0};
const DEFAULT_END_TIME = {hour: 3, minute: 0};

export interface RangePickerRef {
  reset: () => void
}

export const RangePicker = forwardRef<RangePickerRef, DateTimeRangePickerProps>(({
                                                                                   startDate,
                                                                                   endDate,
                                                                                   onChange,
                                                                                   showTime = true,
                                                                                   disabled = false,
                                                                                 }: DateTimeRangePickerProps, ref) => {
  const [open, setOpen] = useState(false)
  const [selectingEnd, setSelectingEnd] = useState(false)
  
  const [internalValue, setInternalValue] = useState<DateTimeRange>({
    startDate: startDate ?? DEFAULT_START_DATE,
    endDate: endDate ?? DEFAULT_END_DATE,
    startTime: startDate ? {hour: startDate.getHours(), minute: startDate.getMinutes()} : DEFAULT_START_TIME,
    endTime: endDate ? {hour: endDate.getHours(), minute: endDate.getMinutes()} : DEFAULT_END_TIME,
  })
  
  const [hoveredValue, setHoveredValue] = useState<DateRange>({
    startDate: startDate ?? DEFAULT_START_DATE, endDate: endDate ?? DEFAULT_END_DATE
  })
  
  useEffect(() => {
    if (!startDate || !endDate) {
      onChange(DEFAULT_START_DATE, DEFAULT_END_DATE)
    }
  }, [startDate, endDate]);
  
  useImperativeHandle(ref, () => ({
    reset: () => {
      setInternalValue((prev) => {
        const value = {
          startDate: DEFAULT_START_DATE,
          endDate: DEFAULT_END_DATE,
          startTime: {hour: 10, minute: 0},
          endTime: {hour: 3, minute: 0}
        }
        return value;
      })
    }
  }))
  
  const handleDateSelect = (range: DateRange) => {
    setInternalValue((prev) => {
      const nextValue = {...prev, ...range};
      if (!!prev.startTime?.hour) nextValue?.startDate?.setHours(prev.startTime.hour)
      if (!!prev.startTime?.minute) nextValue?.startDate?.setMinutes(prev.startTime.minute)
      if (!!prev.endTime?.hour) nextValue?.endDate?.setHours(prev.endTime.hour)
      if (!!prev.endTime?.minute) nextValue?.endDate?.setMinutes(prev.endTime.minute)
      
      onChange(nextValue.startDate, nextValue.endDate)
      return nextValue;
    })
    
    setSelectingEnd(prev => !prev)
  }
  
  const formattedStartDate = formatDate(open && hoveredValue.startDate
    ? hoveredValue.startDate
    : internalValue.startDate);
  const formattedEndDate = formatDate(open && hoveredValue.endDate ? hoveredValue.endDate : internalValue.endDate);
  
  const handleChangeTime = useCallback((time: HourType, type: "start" | "end") => {
    setInternalValue((prev) => {
      let nextValue = {...prev};
      if (type === 'start') {
        nextValue.startTime = time;
      }
      else {
        nextValue.endTime = time;
      }
      if (nextValue.startTime?.hour) nextValue?.startDate?.setHours(nextValue.startTime.hour)
      if (nextValue.startTime?.minute) nextValue?.startDate?.setMinutes(nextValue.startTime.minute)
      if (nextValue.endTime?.hour) nextValue?.endDate?.setHours(nextValue.endTime.hour)
      if (nextValue.endTime?.minute) nextValue?.endDate?.setMinutes(nextValue.endTime.minute)
      
      onChange(nextValue.startDate, nextValue.endDate)
      return nextValue;
    })
  }, []);
  
  const timePicker = showTime && (<div className="h-full w-full flex flex-col p-4 gap-5">
    <div className="p-2">
      <TimePicker
        value={internalValue.startTime || DEFAULT_START_TIME}
        onChange={value => handleChangeTime(value, "start")}
        label={formattedStartDate}
      />
    </div>
    <div className="p-2">
      <TimePicker
        value={internalValue.endTime || DEFAULT_END_TIME}
        onChange={value => handleChangeTime(value, "end")}
        label={formattedEndDate}
      />
    </div>
  </div>)
  
  return (<Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild>
      <Button
        size={"sm"}
        disabled={disabled}>
          <span className={cn(selectingEnd && open ? "text-neutral-400" : "")}
                onClick={() => setSelectingEnd(false)}>
            {`${formattedStartDate} ${String(internalValue.startTime?.hour)
            .padStart(2, "0")}:${String(internalValue.startTime?.minute)
            .padStart(2, "0")}`}
          </span>
        <SwapRight />
        <span className={cn(selectingEnd && open ? "text-neutral-400" : "")}
              onClick={() => setSelectingEnd(true)}>
            {`${formattedEndDate} ${String(internalValue.endTime?.hour)
            .padStart(2, "0")}:${String(internalValue.endTime?.minute)
            .padStart(2, "0")}`}
          </span>
        <CalendarSvg color={"#0005"} className="size-4 text-muted-foreground shrink-0" />
      </Button>
    </PopoverTrigger>
    
    <PopoverContent
      align="start"
      sideOffset={4}
      isFocusable={false}
      className="rounded-lg border border-[rgba(5,5,5,0.06)] bg-white p-0 shadow-[0_6px_16px_0_rgba(0,0,0,0.08),0_3px_6px_-4px_rgba(0,0,0,0.12),0_9px_28px_8px_rgba(0,0,0,0.05)]"
      onPointerDown={(event) => event.stopPropagation()}
    >
      <CalendarPanel
        rangeStart={internalValue.startDate}
        rangeEnd={internalValue.endDate}
        onSelect={handleDateSelect}
        selectingEnd={selectingEnd}
        onHover={setHoveredValue}
        timePicker={timePicker}
      />
    </PopoverContent>
  </Popover>);
});

RangePicker.displayName = "RangePicker";
